/**
 * Bible Mysteries Decoder — W01 Kadosh, per-production overlay composition.
 * AUTO-GENERATED from overlay_plan.json (anchors resolved against
 * productions.word_timestamps_hindi).
 *
 * Devotional route: NO logo here (ffmpeg adds it from channel_config).
 * All times are S(seconds) so the comp is correct at 16fps or 24fps.
 */
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { FactTag, BigMoment } from "./BibleDecoderOverlayFinal";
import { loadFont as loadMono } from "@remotion/google-fonts/IBMPlexMono";

const { fontFamily: mono } = loadMono("normal", { weights: ["400", "500"], subsets: ["latin"] });
const TEXT_SHADOW = "0 2px 6px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.9), 0 0 22px rgba(0,0,0,0.55)";

// Persistent channel wordmark, bottom-right. In-comp (not ffmpeg) so it
// scales with --scale=3, matching how the devotional comps carry their CTA
// pills. Sits at bottom:18, BELOW BigMoment's bottom:44 block, so a reveal
// never collides with it. Pure white per Yash 2026-07-22.
const Wordmark: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const fade = interpolate(frame, [0, Math.round(2.25 * fps)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div
      style={{
        position: "absolute", bottom: 18, right: 46.67,
        fontFamily: mono, fontSize: 11, letterSpacing: 2.4, fontWeight: 500,
        color: "#ffffff", textTransform: "uppercase",
        textShadow: TEXT_SHADOW, opacity: fade,
      }}
    >
      Bible Mysteries Decoder
    </div>
  );
};

// FactTag/BigMoment only fade in. Hold fades the tail so events do not pop out.
const Hold: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = Math.round(0.6 * fps);
  const out = interpolate(frame, [dur - f, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: out }}>{children}</AbsoluteFill>;
};

export const BibleDecoderW01Overlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <Wordmark />
      {/* 01 · FACTTAG · THE WORD · anchor: The word is qadosh */}
      <Sequence from={S(27.386)} durationInFrames={S(3.176)}>
        <Hold dur={S(3.176)}>
          <FactTag kicker="THE WORD" value="Qadosh" />
        </Hold>
      </Sequence>
      {/* 02 · FACTTAG · FREQUENCY · anchor: six hundred times */}
      <Sequence from={S(30.962)} durationInFrames={S(6.0)}>
        <Hold dur={S(6.0)}>
          <FactTag kicker="FREQUENCY" value="600+ occurrences" />
        </Hold>
      </Sequence>
      {/* 03 · BIGMOMENT · THE ROOT · anchor: Its core meaning is separation */}
      <Sequence from={S(43.559)} durationInFrames={S(9.0)}>
        <Hold dur={S(9.0)}>
          <BigMoment kicker="THE ROOT" headline="Set apart, not clean" sub="Qadosh means cut off from the ordinary, removed from common use" />
        </Hold>
      </Sequence>
      {/* 04 · FACTTAG · ROOT · anchor: qoph, dalet, shin */}
      <Sequence from={S(109.948)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="ROOT" value="Q  D  SH" />
        </Hold>
      </Sequence>
      {/* 05 · FACTTAG · COGNATES · anchor: Ugaritic tablets */}
      <Sequence from={S(119.422)} durationInFrames={S(9.0)}>
        <Hold dur={S(9.0)}>
          <FactTag kicker="COGNATES" value="Ugaritic and Akkadian" />
        </Hold>
      </Sequence>
      {/* 06 · FACTTAG · ENGLISH ROOT · anchor: Old English halig */}
      <Sequence from={S(152.858)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="ENGLISH ROOT" value="Halig, whole, health" />
        </Hold>
      </Sequence>
      {/* 07 · FACTTAG · CITATION · anchor: Genesis, chapter two, verse three */}
      <Sequence from={S(192.02)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="CITATION" value="Genesis 2:3" />
        </Hold>
      </Sequence>
      {/* 08 · BIGMOMENT · FIRST HOLY THING · anchor: the seventh day and makes it qadosh */}
      <Sequence from={S(197.361)} durationInFrames={S(9.0)}>
        <Hold dur={S(9.0)}>
          <BigMoment kicker="FIRST HOLY THING" headline="A day, not a place" sub="The seventh day, cut out of the week and marked off from the other six" />
        </Hold>
      </Sequence>
      {/* 09 · FACTTAG · CITATION · anchor: Exodus, chapter three */}
      <Sequence from={S(234.192)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="CITATION" value="Exodus 3:5" />
        </Hold>
      </Sequence>
      {/* 10 · FACTTAG · CITATION · anchor: Exodus, chapter nineteen */}
      <Sequence from={S(294.124)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="CITATION" value="Exodus 19:12" />
        </Hold>
      </Sequence>
      {/* 11 · BIGMOMENT · THE CONSEQUENCE · anchor: Whoever touches the mountain will die */}
      <Sequence from={S(307.951)} durationInFrames={S(9.0)}>
        <Hold dur={S(9.0)}>
          <BigMoment kicker="THE CONSEQUENCE" headline="Holiness draws a kill zone" sub="A fence at the base, and a death sentence for crossing it" />
        </Hold>
      </Sequence>
      {/* 12 · BIGMOMENT · SUPERLATIVE BY REPETITION · anchor: qodesh haqodashim */}
      <Sequence from={S(369.391)} durationInFrames={S(10.0)}>
        <Hold dur={S(10.0)}>
          <BigMoment kicker="SUPERLATIVE BY REPETITION" headline="Qodesh haqodashim" sub="The set apart of the set apart ones, Hebrew builds a superlative by repeating the word" />
        </Hold>
      </Sequence>
      {/* 13 · FACTTAG · CITATION · anchor: Second Samuel, chapter six */}
      <Sequence from={S(416.574)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="CITATION" value="2 Samuel 6:6-7" />
        </Hold>
      </Sequence>
      {/* 14 · FACTTAG · PERSON · anchor: a man named Uzzah */}
      <Sequence from={S(430.412)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="PERSON" value="Uzzah" />
        </Hold>
      </Sequence>
      {/* 15 · FACTTAG · CITATION · anchor: Leviticus, chapter ten */}
      <Sequence from={S(480.963)} durationInFrames={S(4.07)}>
        <Hold dur={S(4.07)}>
          <FactTag kicker="CITATION" value="Leviticus 10:1-2" />
        </Hold>
      </Sequence>
      {/* 16 · FACTTAG · PRIESTS · anchor: Nadab and Abihu */}
      <Sequence from={S(485.433)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="PRIESTS" value="Nadab and Abihu" />
        </Hold>
      </Sequence>
      {/* 17 · BIGMOMENT · THE BREACH · anchor: strange fire */}
      <Sequence from={S(492.004)} durationInFrames={S(9.0)}>
        <Hold dur={S(9.0)}>
          <BigMoment kicker="THE BREACH" headline="Casual is what the boundary does not survive" sub="Not wickedness, an improvised shortcut into the set apart space" />
        </Hold>
      </Sequence>
      {/* 18 · FACTTAG · CITATION · anchor: Be holy, because I am holy */}
      <Sequence from={S(543.098)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="CITATION" value="Leviticus 19:2" />
        </Hold>
      </Sequence>
      {/* 19 · BIGMOMENT · THE CONTROL WORD · anchor: It is tahor */}
      <Sequence from={S(558.966)} durationInFrames={S(10.0)}>
        <Hold dur={S(10.0)}>
          <BigMoment kicker="THE CONTROL WORD" headline="Tahor is not qadosh" sub="Tahor is your condition. Qadosh is your category. Hebrew keeps both words because they differ" />
        </Hold>
      </Sequence>
      {/* 20 · FACTTAG · CITATION · anchor: Isaiah, chapter six */}
      <Sequence from={S(603.325)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="CITATION" value="Isaiah 6:3" />
        </Hold>
      </Sequence>
      {/* 21 · FACTTAG · CITATION · anchor: Woe is me */}
      <Sequence from={S(630.458)} durationInFrames={S(6.0)}>
        <Hold dur={S(6.0)}>
          <FactTag kicker="CITATION" value="Isaiah 6:5" />
        </Hold>
      </Sequence>
      {/* 22 · BIGMOMENT · THE REVEAL · anchor: There is a Hebrew word, qedeshah */}
      <Sequence from={S(742.836)} durationInFrames={S(11.0)}>
        <Hold dur={S(11.0)}>
          <BigMoment kicker="THE REVEAL" headline="Qedeshah" sub="Same root, Q D SH, the word your translators called shrine prostitute" />
        </Hold>
      </Sequence>
      {/* 23 · FACTTAG · CITATION · anchor: Genesis, chapter thirty eight */}
      <Sequence from={S(826.704)} durationInFrames={S(6.648)}>
        <Hold dur={S(6.648)}>
          <FactTag kicker="CITATION" value="Genesis 38:21" />
        </Hold>
      </Sequence>
      {/* 24 · FACTTAG · PERSON · anchor: Tamar in disguise */}
      <Sequence from={S(833.752)} durationInFrames={S(6.0)}>
        <Hold dur={S(6.0)}>
          <FactTag kicker="PERSON" value="Tamar" />
        </Hold>
      </Sequence>
      {/* 25 · FACTTAG · CITATION · anchor: Deuteronomy bans the practice */}
      <Sequence from={S(842.877)} durationInFrames={S(3.049)}>
        <Hold dur={S(3.049)}>
          <FactTag kicker="CITATION" value="Deuteronomy 23:17" />
        </Hold>
      </Sequence>
      {/* 26 · FACTTAG · CITATION · anchor: The prophet Hosea uses it */}
      <Sequence from={S(846.326)} durationInFrames={S(6.0)}>
        <Hold dur={S(6.0)}>
          <FactTag kicker="CITATION" value="Hosea 4:14" />
        </Hold>
      </Sequence>
      {/* 27 · FACTTAG · MANUSCRIPT · anchor: The Dead Sea Scrolls */}
      <Sequence from={S(873.833)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="MANUSCRIPT" value="Dead Sea Scrolls" />
        </Hold>
      </Sequence>
      {/* 28 · FACTTAG · MANUSCRIPT · anchor: The ancient Greek translation */}
      <Sequence from={S(881.635)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="MANUSCRIPT" value="Septuagint, 3rd c. BC" />
        </Hold>
      </Sequence>
      {/* 29 · BIGMOMENT · THE HANDOFF · anchor: rendered in Greek by hagios */}
      <Sequence from={S(921.872)} durationInFrames={S(11.0)}>
        <Hold dur={S(11.0)}>
          <BigMoment kicker="THE HANDOFF" headline="Qadosh, hagios, sanctus, halig" sub="Four separate words across four languages, and a little of the danger left behind at every border" />
        </Hold>
      </Sequence>
      {/* 30 · FACTTAG · PLACE · anchor: a station called Kadesh */}
      <Sequence from={S(1001.474)} durationInFrames={S(7.0)}>
        <Hold dur={S(7.0)}>
          <FactTag kicker="PLACE" value="Kadesh, the set apart place" />
        </Hold>
      </Sequence>
      {/* 31 · BIGMOMENT · NEW TESTAMENT · anchor: call ordinary believers hagioi */}
      <Sequence from={S(1044.792)} durationInFrames={S(10.0)}>
        <Hold dur={S(10.0)}>
          <BigMoment kicker="NEW TESTAMENT" headline="Hagioi, the set apart ones" sub="Rendered saints in English. The word is about their category, not their character" />
        </Hold>
      </Sequence>
      {/* 32 · BIGMOMENT · MYTH BUST · anchor: a rope tied around his ankle */}
      <Sequence from={S(1127.352)} durationInFrames={S(10.0)}>
        <Hold dur={S(10.0)}>
          <BigMoment kicker="MYTH BUST" headline="The rope is fiction" sub="Not in the text, not in the earliest records, it first appears more than a thousand years later" />
        </Hold>
      </Sequence>
      {/* 33 · BIGMOMENT · THE VERDICT · anchor: Here is the verdict */}
      <Sequence from={S(1160.046)} durationInFrames={S(11.0)}>
        <Hold dur={S(11.0)}>
          <BigMoment kicker="THE VERDICT" headline="One is a compliment. The other is a boundary." sub="Your English says holy. The Hebrew says cut off, set apart, and dangerous to touch" />
        </Hold>
      </Sequence>
      {/* 34 · BIGMOMENT · QADOSH · anchor: Qadosh. Not clean. Cut apart. */}
      <Sequence from={S(1312.809)} durationInFrames={S(10.0)}>
        <Hold dur={S(10.0)}>
          <BigMoment kicker="QADOSH" headline="Not clean. Cut apart." sub="One buried word at a time" />
        </Hold>
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
